import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  dbCarToApp, appCarToDb, dbReqToApp, appReqToDb,
  apiGetCars, apiGetRequests, apiGetWizardConfig, apiSaveCar, apiDeleteCar, apiPatchCarOrder,
  apiSaveRequest, apiUpdateRequest, apiDeleteRequest, apiSaveWizardConfig,
} from "./api";
import DEFAULT_WIZARD_CONFIG from "./data/wizardDefaults";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SpinnerIcon from "./components/SpinnerIcon";
import Home from "./pages/Home";
import Inventory from "./pages/Inventory";
import CarDetail from "./pages/CarDetail";
import ImportWizard from "./pages/ImportWizard";
import Cleaning from "./pages/Cleaning";
import About from "./pages/About";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";

const AdminPage = lazy(() => import("./admin/AdminPage"));

// Links from the previous hash-based site (/#nabidka …) keep working.
const LEGACY_HASHES = { "#nabidka": "/nabidka", "#dovoz": "/dovoz", "#o-nas": "/o-nas", "#kontakt": "/kontakt", "#cisteni": "/cisteni" };

function ScrollManager() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (LEGACY_HASHES[hash]) { navigate(LEGACY_HASHES[hash], { replace: true }); return; }
    if (hash) {
      // Wait a frame so the target section has rendered.
      requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash, navigate]);

  return null;
}

export default function App() {
  const [cars, setCars] = useState([]);
  const [carsStatus, setCarsStatus] = useState("loading"); // loading | ready | error
  const [wizardConfig, setWizardConfig] = useState(DEFAULT_WIZARD_CONFIG);
  const [requests, setRequests] = useState([]);

  // Public data on mount. Requests are admin-only and load after login.
  useEffect(() => {
    apiGetCars()
      .then((rows) => { setCars((rows || []).map(dbCarToApp)); setCarsStatus("ready"); })
      .catch(() => setCarsStatus("error"));
    apiGetWizardConfig()
      .then((cfg) => cfg?.config && setWizardConfig(cfg.config))
      .catch(() => {});
  }, []);

  const refreshRequests = async () => {
    try {
      const data = await apiGetRequests();
      if (data?.length) setRequests(data.map(dbReqToApp));
    } catch (_) {}
  };

  // ── Cars CRUD ─────────────────────────────────────────────────────────────
  const saveCar = async (car, editId) => {
    const data = await apiSaveCar(appCarToDb(car), editId);
    if (data) {
      const formatted = dbCarToApp(data);
      if (editId) setCars((cs) => cs.map((c) => (c.id === editId ? formatted : c)));
      else setCars((cs) => [...cs, formatted]);
    }
  };

  const deleteCarById = async (id) => {
    await apiDeleteCar(id);
    setCars((cs) => cs.filter((c) => c.id !== id));
  };

  // visibleIds: when the admin list is filtered, swap with the nearest *visible* neighbour.
  const moveCar = async (carId, direction, visibleIds) => {
    const sorted = [...cars].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const scope = visibleIds ? sorted.filter((c) => visibleIds.includes(c.id)) : sorted;
    const idx = scope.findIndex((c) => c.id === carId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swapIdx < 0 || swapIdx >= scope.length) return;
    const ids = sorted.map((c) => c.id);
    const a = ids.indexOf(carId);
    const b = ids.indexOf(scope[swapIdx].id);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    // Normalise to sequential integers and persist every row whose order changed
    const norm = ids.map((id, i) => ({ id, sort_order: i }));
    const changed = norm.filter((n) => cars.find((c) => c.id === n.id).sort_order !== n.sort_order);
    await Promise.all(changed.map((n) => apiPatchCarOrder(n.id, n.sort_order)));
    setCars((cs) => cs.map((c) => {
      const entry = norm.find((n) => n.id === c.id);
      return entry ? { ...c, sort_order: entry.sort_order } : c;
    }));
  };

  // ── Requests CRUD ─────────────────────────────────────────────────────────
  // Throws when the API fails so the forms can show an error instead of a fake success.
  const addRequest = async (req) => {
    await apiSaveRequest(appReqToDb(req));
    setRequests((rs) => [req, ...rs]);
  };

  const updateRequestById = async (id, patch) => {
    await apiUpdateRequest(id, appReqToDb(patch));
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const deleteRequestById = async (id) => {
    await apiDeleteRequest(id);
    setRequests((rs) => rs.filter((r) => r.id !== id));
  };

  const saveWizardConfig = async (config) => {
    await apiSaveWizardConfig(config);
    setWizardConfig(config);
  };

  const carProps = { cars, carsStatus };

  return (
    <>
      <a href="#main" className="skip-link">Přeskočit na obsah</a>
      <ScrollManager />
      <Header />
      <main id="main" style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<Home {...carProps} wizardConfig={wizardConfig} />} />
          <Route path="/nabidka" element={<Inventory {...carProps} />} />
          <Route path="/nabidka/:slug" element={<CarDetail {...carProps} />} />
          <Route path="/dovoz" element={<ImportWizard wizardConfig={wizardConfig} addRequest={addRequest} />} />
          <Route path="/cisteni" element={<Cleaning />} />
          <Route path="/o-nas" element={<About />} />
          <Route path="/kontakt" element={<Contact addRequest={addRequest} />} />
          <Route path="/admin" element={<Navigate to="/sprava" replace />} />
          <Route path="/sprava" element={
            <Suspense fallback={<div style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}><SpinnerIcon size={48} /></div>}>
              <AdminPage
                cars={cars} saveCar={saveCar} deleteCarById={deleteCarById} moveCar={moveCar}
                wizardConfig={wizardConfig} saveWizardConfig={saveWizardConfig}
                requests={requests} updateRequest={updateRequestById} deleteRequest={deleteRequestById}
                refreshRequests={refreshRequests}
              />
            </Suspense>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}
